import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import {
  NetworkVisibility,
  Space,
  SpaceMembershipStatus,
  SpaceType,
} from '@tribeplatform/gql-client/types'
import { useRouterPath } from '@tribeplatform/react-sdk'
import {
  useAuthMember,
  useAuthToken,
  useLeaveSpace,
} from '@tribeplatform/react-sdk/hooks'
import { type SvgIconName } from '@tribeplatform/react-ui-kit/Icon'

import { useActionItems } from '../../ActionItems/index.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'

export type SpaceActionItem = {
  name: string
  url?: string
  icon?: SvgIconName
  action?: (props?: unknown) => void
  category?: string
  hotkey?: string
}

export const useSpaceActionItems = ({ space }: { space: Space }) => {
  const { $t } = useI18n()
  const { isGuest } = useAuthMember()

  const { openModal } = useActionItems()

  const actionItems: SpaceActionItem[] = []

  const { mutate: leave } = useLeaveSpace()

  const joined =
    space?.authMemberProps?.membershipStatus === SpaceMembershipStatus.joined

  const {
    data: { network },
  } = useAuthToken()

  const [canUpdateNetwork] = hasScopesPermission(network, [
    'updateNetwork',
    'createTemplate',
  ])
  const [canUpdateSpace, canAddSpaceMembers] = hasScopesPermission(space, [
    'updateSpace',
    'addSpaceMembers',
  ])

  const itemCategoryText = $t({
    id: 'Generics.Space',
    defaultMessage: '{SPACE_CC}',
  })

  const pathname = useRouterPath()
  const spacePath = space?.relativeUrl
  const isSpacePage = pathname === spacePath

  if (!space?.id) {
    return { actions: [], children: null }
  }

  actionItems.push({
    action: () =>
      openModal('space', 'shareModal', {
        isPrivate:
          space?.hidden || network.visibility === NetworkVisibility.private,
        spaceId: space?.id,
        title: space.name,
        url: space?.url,
      }),
    icon: 'share',
    name: $t({
      id: 'Space.UseSpaceActionItems.Items.ShareSpace',
      defaultMessage: 'Share {SPACE}',
    }),
    category: itemCategoryText,
    hotkey: 'option+s,alt+s',
  })

  if (!isGuest) {
    if (!isSpacePage) {
      actionItems.push({
        url: spacePath,
        icon: 'grid',
        name: $t({
          id: 'Space.Actions.View',
          defaultMessage: 'View {SPACE}',
        }),
        category: itemCategoryText,
      })
    }

    if (canAddSpaceMembers) {
      actionItems.push({
        action: () => openModal('space', 'spaceMembersModal', { space }),
        icon: 'members',
        name: 'View members',
        category: 'Space',
      })
    }

    if (canUpdateSpace) {
      actionItems.push({
        url:
          space?.type === SpaceType.Page
            ? `/settings/moderation`
            : `/settings/moderation/${space.id}/pending-posts`,
        icon: 'moderation',
        name: $t({
          id: 'Generics.Moderation',
          defaultMessage: 'Moderation',
        }),
        category: itemCategoryText,
        hotkey: 'm',
      })
    }

    if (canUpdateSpace) {
      actionItems.push({
        action: () =>
          openModal('space', 'spaceAnalyticsModal', {
            space,
            linkToContent: false,
          }),
        icon: 'analytics',
        name: $t({
          id: 'Space.UseSpaceActionItems.Items.SpaceAnalytics',
          defaultMessage: '{SPACE_CC} analytics',
        }),
        hotkey: 'option+a,alt+a',
        category: itemCategoryText,
      })
    }

    if (canUpdateSpace && canUpdateNetwork) {
      actionItems.push({
        action: () => {
          openModal('space', 'auditLogsModal', {
            filters: { spaceId: space?.id },
          })
        },
        icon: 'audit-log',
        name: $t({
          id: 'Generics.AuditLogs',
          defaultMessage: 'Audit logs',
        }),
        hotkey: 'option+l,alt+l',
        category: itemCategoryText,
      })
    }

    if (canUpdateNetwork) {
      actionItems.push({
        url: adminRoutes.design.customizer.spaceBlocks(space.id),
        icon: 'customizer',
        name: $t({
          id: 'Generics.Customize',
          defaultMessage: 'Customize',
        }),
        category: itemCategoryText,
        hotkey: 'c',
      })
    }

    if (joined) {
      actionItems.push({
        action: () => leave({ spaceId: space?.id }),
        icon: 'logout',
        name: $t({
          id: 'Space.UseSpaceActionItems.Items.LeaveSpace',
          defaultMessage: 'Leave {SPACE}',
        }),
        category: itemCategoryText,
      })
    }
  }

  return { actions: actionItems }
}
